import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ENDPOINTS from "../../config/apiUrls";
import axiosInstance from "../../config/axiosInstance";
import { normalizePinnedMessage } from "../../Pages/Dashboard/Moderator/Chat/pinnedMessageUtils";
import { getOffset, getToken, isOnline } from "../../helpers/utils";
import { postRequest } from "../../config/dataApi";



export const getOnlineUsersTillNow = createAsyncThunk(
    "chat/getOnlineUsersTillNow",
    async ({ page = 1, limit = 10 }, { getState }) => {
        try {
            const { auth } = getState();
            const roomId = auth?.user?.attributes?.stationChatId;
            const response = await postRequest(ENDPOINTS.CHAT.USERS, { roomId, limit, offset: page == 1 ? 0 : getOffset(page, limit) });
            return response;
        } catch (error) {
            throw new Error(error.response?.data?.message || "Failed to fetch users");
        }
    }
);


export const loadMessageHistory = createAsyncThunk(
    "chat/loadMessageHistory",
    async (_, { getState, rejectWithValue }) => {
        const { auth, chat } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;
        const lastMessageId = chat.meta?.before; // Get last message ID
        try {
            const payload = { roomId };

            if (lastMessageId) {
                payload.before = lastMessageId;
            }


            const response = await axiosInstance.post(ENDPOINTS.CHAT.HISTORY, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const pinMessage = createAsyncThunk(
    "chat/pin-message",
    async (id, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const token = getToken();
        const roomId = auth?.user?.attributes?.stationChatId;
        try {
            const payload = { roomId, id, token };

            const response = await axiosInstance.post(ENDPOINTS.CHAT.PIN_MESSAGE, payload);
            return { id, ...response.data };
            
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const removePinMessage = createAsyncThunk(
    "chat/removePinMessage-message",
    async (id, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const token = getToken();
        const roomId = auth?.user?.attributes?.stationChatId;
        try {
            const payload = { roomId, token };

            const response = await axiosInstance.post(ENDPOINTS.CHAT.UNPIN_MESSAGE, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateMessage = createAsyncThunk(
    "chat/update-message",
    async ({ id, newText }, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const token = getToken();
        const roomId = auth?.user?.attributes?.stationChatId;
        try {
            const payload = { roomId, token, text: newText, id };

            await axiosInstance.post(ENDPOINTS.CHAT.UPDATE_MESSAGE, payload);
            return { id, newText };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteChatMessage = createAsyncThunk(
    "chat/delete-message",
    async ({ id }, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const token = getToken();
        const roomId = auth?.user?.attributes?.stationChatId;
        try {
            const payload = { roomId, token, id };
            await axiosInstance.post(ENDPOINTS.CHAT.DELETE_MESSAGE, payload);
            return { id };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const scheduleChatMessage = createAsyncThunk(
    "chat/scheduleChatMessage",
    async (
        {
            message: successMessage = "Message scheduled successfully.",
            text,
            pinMessage = false,
            scheduledAt,
            scheduledTimezone,
            scheduledLocalDate,
            scheduledLocalTime,
            scheduledPinRemovalAt,
            scheduledPinRemovalTimezone,
            scheduledPinRemovalLocalDate,
            scheduledPinRemovalLocalTime,
        },
        { getState, rejectWithValue }
    ) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;

        if (!roomId) {
            return rejectWithValue("Chat room not found.");
        }
        if (!scheduledAt) {
            return rejectWithValue("Scheduled time is required.");
        }

        try {
            const payload = {
                roomId,
                scheduledAt,
                scheduledTimezone,
                scheduledLocalDate,
                scheduledLocalTime,
                message: {
                    text,
                    type: pinMessage ? "sticky" : undefined,
                    media: "",
                },
            };

            if (pinMessage && scheduledPinRemovalAt) {
                payload.scheduledPinRemovalAt = scheduledPinRemovalAt;
                payload.scheduledPinRemovalTimezone = scheduledPinRemovalTimezone;
                payload.scheduledPinRemovalLocalDate = scheduledPinRemovalLocalDate;
                payload.scheduledPinRemovalLocalTime = scheduledPinRemovalLocalTime;
            }

            const response = await axiosInstance.post(ENDPOINTS.CHAT.SCHEDULE_MESSAGE, payload);

            if (response.status === 200) {
                toast.success(successMessage, {
                    position: "top-right",
                    autoClose: 3000,
                });
                return response.data;
            }

            return rejectWithValue("Failed to schedule message.");
        } catch (error) {
            const errMsg = error.response?.data?.message || error.message || "Failed to schedule message.";
            toast.error(errMsg, {
                position: "top-right",
                autoClose: 3000,
            });
            return rejectWithValue(errMsg);
        }
    }
);

export const fetchScheduledChatMessages = createAsyncThunk(
    "chat/fetchScheduledChatMessages",
    async ({ status, active = false } = {}, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;

        if (!roomId) {
            return rejectWithValue("Chat room not found.");
        }

        try {
            const payload = { roomId };
            if (active) {
                payload.active = true;
            } else if (status) {
                payload.status = status;
            }

            const response = await axiosInstance.post(ENDPOINTS.CHAT.SCHEDULE_MESSAGE_LIST, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to fetch scheduled messages."
            );
        }
    }
);

export const updateScheduledChatMessage = createAsyncThunk(
    "chat/updateScheduledChatMessage",
    async (
        {
            id,
            message: successMessage = "Scheduled message updated successfully.",
            text,
            scheduledAt,
            scheduledTimezone,
            scheduledLocalDate,
            scheduledLocalTime,
            scheduledPinRemovalAt,
            scheduledPinRemovalTimezone,
            scheduledPinRemovalLocalDate,
            scheduledPinRemovalLocalTime,
            schedulePinRemoval,
            pinMessage,
        },
        { getState, rejectWithValue }
    ) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;

        if (!roomId || !id) {
            return rejectWithValue("Invalid scheduled message.");
        }

        try {
            const payload = {
                roomId,
                id,
                text,
                scheduledAt,
                scheduledTimezone,
                scheduledLocalDate,
                scheduledLocalTime,
            };

            if (schedulePinRemoval !== undefined) {
                payload.schedulePinRemoval = schedulePinRemoval;
            }
            if (pinMessage !== undefined) {
                payload.pinMessage = pinMessage;
            }

            if (scheduledPinRemovalAt !== undefined) {
                payload.scheduledPinRemovalAt = scheduledPinRemovalAt;
                payload.scheduledPinRemovalTimezone = scheduledPinRemovalTimezone;
                payload.scheduledPinRemovalLocalDate = scheduledPinRemovalLocalDate;
                payload.scheduledPinRemovalLocalTime = scheduledPinRemovalLocalTime;
            }

            const response = await axiosInstance.post(ENDPOINTS.CHAT.SCHEDULE_MESSAGE_UPDATE, payload);

            if (response.status === 200) {
                toast.success(successMessage, {
                    position: "top-right",
                    autoClose: 3000,
                });
                return response.data;
            }

            return rejectWithValue("Failed to update scheduled message.");
        } catch (error) {
            const errMsg =
                error.response?.data?.message || error.message || "Failed to update scheduled message.";
            toast.error(errMsg, {
                position: "top-right",
                autoClose: 3000,
            });
            return rejectWithValue(errMsg);
        }
    }
);

export const cancelScheduledChatMessage = createAsyncThunk(
    "chat/cancelScheduledChatMessage",
    async ({ id, message: successMessage = "Scheduled message cancelled." }, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;

        if (!roomId || !id) {
            return rejectWithValue("Invalid scheduled message.");
        }

        try {
            const response = await axiosInstance.post(ENDPOINTS.CHAT.SCHEDULE_MESSAGE_CANCEL, {
                roomId,
                id,
            });

            if (response.status === 200) {
                toast.success(successMessage, {
                    position: "top-right",
                    autoClose: 3000,
                });
                return { id };
            }

            return rejectWithValue("Failed to cancel scheduled message.");
        } catch (error) {
            const errMsg =
                error.response?.data?.message || error.message || "Failed to cancel scheduled message.";
            toast.error(errMsg, {
                position: "top-right",
                autoClose: 3000,
            });
            return rejectWithValue(errMsg);
        }
    }
);

export const joinRoom = createAsyncThunk('chat/join-room', async (socket, { getState, dispatch }) => {
    try {
        const { user } = getState().auth;
        const roomId = user.attributes.stationChatId;

        socket.emit('chat.rooms.join', { id: roomId, extra: 'sss' });
        return { success: true };
    } catch (error) {
        throw new Error(error.message);
    }
});

const chatSlice = createSlice({
    name: "chat",
    initialState: {
        roomSize: 0, // Stores room sizes by room ID
        onlineUsers: [],
        guestUsers: 0,
        messages: [],
        isSearching: false,
        newMessageReceived: false,
        pinnedMessage: null,
        filteredMessages: [],
        filteredOnlineUsers: [],
        blockedUsers: [],
        scheduleMessageLoading: false,
        scheduleMessageError: null,
        scheduledMessages: [],
        scheduledMessagesLoading: false,
        meta: {
            count: 0,
            last: null,
            before: null,
            hasMore: true,
        }, // Stores messages per room
        activeRoom: null, // Currently active chat room
    },
    reducers: {
        setActiveRoom: (state, action) => {
            state.activeRoom = action.payload;
        },
        newMessage: (state, action) => {
            const { data } = action.payload;
            state.messages.push(data);
            state.filteredMessages.push(data);
            state.newMessageReceived = true; // 🔹 Set flag when new message appends
        },
        setNewMessageReceived: (state, action) => {
            state.newMessageReceived = action.payload;
        },
        filterMessages: (state, action) => {
            const searchQuery = action.payload.toLowerCase();
            state.filteredMessages = state.messages.filter(
                (msg) => msg?.attributes?.text?.toLowerCase().includes(searchQuery)
            );
        },
        searchOnlineUsers: (state, action) => {
            const query = action.payload.trim().toLowerCase();

            if (!query) {
                // reset back to all users when field is cleared
                state.filteredOnlineUsers = [...state.onlineUsers];
            } else {
                state.filteredOnlineUsers = state.onlineUsers.filter((user) => {
                    const name = user.attributes.name || "";
                    return name.toLowerCase().includes(query);
                });
            }
        },
        updateSearchingAction: (state, action) => {
            state.isSearching = action.payload;

        },
        messageReceived: (state, action) => {
            state.messages.push(action.payload);
        },
        getRoomSize: (state, action) => {
            const totalRoomSize = action.payload ?? 0;
            const loggedInUserCount = state.onlineUsers?.length ?? 0;
            const guestCount = totalRoomSize - loggedInUserCount;

            state.guestUsers = guestCount ?? 0;  // prevent negative numbers
            state.roomSize = totalRoomSize;  // prevent negative numbers
        },
        updatePinnedMessage: (state, action) => {
            state.pinnedMessage = normalizePinnedMessage(action.payload);
        },
        roomEvent: (state, action) => {
            const { eventIs, payload } = action.payload;

            if (!payload || !payload.attributes) return;

            const isGuest = payload.attributes.roles.includes("guest");
            if (!isGuest) {
                if (eventIs === "join") {
                    const userExists = state.onlineUsers.some((user) => user.id === payload.id);
                    if (!userExists) {
                        state.onlineUsers.push(payload);
                    }
                } else if (eventIs === "leave") {
                    state.onlineUsers = state.onlineUsers.filter((user) => user.id !== payload.id);
                }
            } else {
                if (eventIs === "join") {
                    state.guestUsers += 1;
                } else if (eventIs === "leave" && state.guestUsers > 0) {
                    state.guestUsers -= 1;
                }
            }
        },
        resetChatState: (state) => {
            state.roomSize = 0;
            state.onlineUsers = [];
            state.guestUsers = 0;
            state.messages = [];
            state.isSearching = false;
            state.newMessageReceived = false;
            state.pinnedMessage = null;
            state.filteredMessages = [];
            state.filteredOnlineUsers = [];
            state.blockedUsers = [];
            state.scheduleMessageLoading = false;
            state.scheduleMessageError = null;
            state.scheduledMessages = [];
            state.scheduledMessagesLoading = false;
            state.meta = { count: 0, last: null, before: null, hasMore: true };
            state.activeRoom = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(scheduleChatMessage.pending, (state) => {
                state.scheduleMessageLoading = true;
                state.scheduleMessageError = null;
            })
            .addCase(scheduleChatMessage.fulfilled, (state, action) => {
                state.scheduleMessageLoading = false;
                state.scheduleMessageError = null;
                const created = action.payload?.data;
                if (created) {
                    state.scheduledMessages = [created, ...state.scheduledMessages];
                }
            })
            .addCase(scheduleChatMessage.rejected, (state, action) => {
                state.scheduleMessageLoading = false;
                state.scheduleMessageError = action.payload;
            })
            .addCase(fetchScheduledChatMessages.pending, (state) => {
                state.scheduledMessagesLoading = true;
            })
            .addCase(fetchScheduledChatMessages.fulfilled, (state, action) => {
                state.scheduledMessagesLoading = false;
                state.scheduledMessages = action.payload?.data || [];
            })
            .addCase(fetchScheduledChatMessages.rejected, (state) => {
                state.scheduledMessagesLoading = false;
                state.scheduledMessages = [];
            })
            .addCase(updateScheduledChatMessage.pending, (state) => {
                state.scheduleMessageLoading = true;
            })
            .addCase(updateScheduledChatMessage.rejected, (state) => {
                state.scheduleMessageLoading = false;
            })
            .addCase(updateScheduledChatMessage.fulfilled, (state, action) => {
                state.scheduleMessageLoading = false;
                const updated = action.payload?.data;
                if (!updated?.id) return;

                const attrs = updated.attributes || {};
                if (attrs.deleted) {
                    state.scheduledMessages = state.scheduledMessages.filter(
                        (item) => item.id !== updated.id
                    );
                    return;
                }

                const stillActive =
                    attrs.status === "pending" ||
                    (attrs.status === "sent" &&
                        attrs.scheduledPinRemovalAt &&
                        !attrs.pinRemovalCompleted);

                if (stillActive) {
                    state.scheduledMessages = state.scheduledMessages.map((item) =>
                        item.id === updated.id ? updated : item
                    );
                } else {
                    state.scheduledMessages = state.scheduledMessages.filter(
                        (item) => item.id !== updated.id
                    );
                }
            })
            .addCase(cancelScheduledChatMessage.fulfilled, (state, action) => {
                const { id } = action.payload;
                state.scheduledMessages = state.scheduledMessages.filter((item) => item.id !== id);
            })
            .addCase(loadMessageHistory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadMessageHistory.fulfilled, (state, action) => {
                state.isLoading = false;

                const pinnedMessage = action.payload.data.find(msg => msg.type === 'pin') || null;

                const filteredMessages = action.payload.data
                    .filter(msg => msg.type !== 'pin')
                    .sort((a, b) => new Date(a.attributes?.createdAt) - new Date(b?.attributes?.createdAt));

                state.messages = [...filteredMessages, ...state.messages];
                state.filteredMessages = [...filteredMessages, ...state.filteredMessages];

                if (pinnedMessage) {
                    state.pinnedMessage = normalizePinnedMessage(pinnedMessage);
                }

                state.meta = {
                    count: filteredMessages.length,
                    before: action?.payload?.meta?.last ?? null,
                    hasMore: filteredMessages.length > 0, // ✅ add this
                };
            })
            .addCase(pinMessage.fulfilled, (state, action) => {
                const { data, id } = action.payload;
                state.pinnedMessage = normalizePinnedMessage({ ...data, id });
            })
            .addCase(pinMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removePinMessage.fulfilled, (state, action) => {
                state.pinnedMessage = null;
            })
            .addCase(removePinMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            })
            .addCase(updateMessage.fulfilled, (state, action) => {
                const { id, newText } = action.payload;

                state.messages = state.messages.map((msg) =>
                    msg.id === id ? { ...msg, attributes: { ...msg.attributes, sanitizedText: newText, text: newText } } : msg
                );

                state.filteredMessages = state.filteredMessages.map((msg) =>
                    msg.id === id ? { ...msg, attributes: { ...msg.attributes, sanitizedText: newText, text: newText } } : msg
                );
            })
            .addCase(deleteChatMessage.fulfilled, (state, action) => {
                const { id } = action.payload;
                const pinnedMessage = state.pinnedMessage;
                if(pinnedMessage && pinnedMessage.id == id){
                    state.pinnedMessage = null;
                }
                state.messages = state.messages.filter((msg) => msg.id !== id);
                state.filteredMessages = state.filteredMessages.filter((msg) => msg.id !== id);
            })
            .addCase(deleteChatMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            })
            .addCase(getOnlineUsersTillNow.fulfilled, (state, action) => {
                const data = action.payload.data;
                const prevOnlineUsers = data.filter(isOnline);

                prevOnlineUsers.forEach((user) => {
                    const userExists = state.onlineUsers.some(
                        (existingUser) => existingUser.id === user.id
                    );
                    if (!userExists) {
                        state.onlineUsers.push(user);
                    }
                });

                // keep a copy for search
                state.filteredOnlineUsers = [...state.onlineUsers];
            })



    },
});

export const { setActiveRoom, newMessage, setNewMessageReceived, filterMessages, searchOnlineUsers, updateSearchingAction, messageReceived, getRoomSize, roomEvent, updatePinnedMessage, resetChatState } = chatSlice.actions;
export default chatSlice.reducer;
